import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, ApiError } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { Notification } from '../types/auth';
import '../styles/NotificationBell.css';
import { formatDate } from '../utils/dateUtils';

interface NotificationBellProps {
  onSelectCompany?: (companyId: number) => void;
  refreshTrigger?: boolean;
  isAdmin?: boolean; // Determines if this is for admin or company user
}

const NotificationBell: React.FC<NotificationBellProps> = ({ 
  onSelectCompany, 
  refreshTrigger, 
  isAdmin = true 
}) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);

  // Load notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      let notificationsData: Notification[];
      
      if (isAdmin) {
        // Admin users get all notifications
        notificationsData = await authApi.getAllNotifications(token);
      } else {
        // Company users get notifications for their company
        const userData = authStorage.getUserData();
        if (!userData || userData.companyId === null) {
          setError('Company information not found. Please log in again.');
          return;
        }
        notificationsData = await authApi.getNotificationsByCompany(token, userData.companyId);
      }
      
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load notifications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load notifications on component mount and when refreshTrigger changes
  useEffect(() => {
    loadNotifications();
  }, [refreshTrigger]);

  // Mark notification as read
  const handleNotificationClick = async (notification: Notification) => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Mark as read if it's active (unread)
      if (notification.isActive) {
        await authApi.markNotificationAsRead(token, notification.id);
        // Refresh notifications list after marking as read
        await loadNotifications();
      }

      // Select the company if callback provided (for admin users)
      if (onSelectCompany && isAdmin) {
        onSelectCompany(notification.companyId);
      }

      // Close modal
      setShowModal(false);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to mark notification as read. Please try again.');
      }
    }
  };

  // Get count of active (unread) notifications
  const activeNotificationCount = notifications.filter(n => n.isActive).length;

  // Format date for display with relative time
  const formatNotificationDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
      
      return formatDate(date.toISOString());
    } catch {
      return 'Recently';
    }
  };

  return (
    <>
      {/* Notification Bell Button */}
      <div className="notification-bell-container">
        <button
          className="notification-bell"
          onClick={() => setShowModal(true)}
          title={t('notifications.title', 'Notifications') || 'Notifications'}
        >
          <span className="bell-icon"></span>
          {activeNotificationCount > 0 && (
            <span className="notification-badge">
              {activeNotificationCount > 99 ? '99+' : activeNotificationCount}
            </span>
          )}
        </button>
      </div>

      {/* Notifications Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="notification-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('notifications.title', 'Notifications')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="loading-state">
                  <p>{t('common.loading', 'Loading notifications...')}</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p className="error-message">{error}</p>
                  <button onClick={loadNotifications} className="retry-button">
                    {t('common.retry', 'Retry')}
                  </button>
                </div>
              ) : notifications.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon"></span>
                  <p>{t('notifications.empty', 'No notifications yet')}</p>
                </div>
              ) : (
                <div className="notifications-list">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.isActive ? 'unread' : 'read'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-content">
                        <div className="notification-header">
                          <h4 className="notification-title">{notification.title}</h4>
                          <span className="notification-time">{formatNotificationDate(notification.lastUpdatedAt)}</span>
                        </div>
                        
                        <p className="notification-description">{notification.description}</p>
                        
                        <div className="notification-company">
                          <span className="company-label">{t('notifications.company', 'Company')}:</span>
                          <span className="company-name">{notification.company.companyName}</span>
                          <span className="org-number">({notification.company.orgNumber})</span>
                        </div>
                        
                        {notification.message && (
                          <p className="notification-message">{notification.message}</p>
                        )}

                        {notification.isActive && (
                          <span className="unread-indicator"></span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {notifications.length > 0 && (
              <div className="modal-footer">
                <button onClick={loadNotifications} className="refresh-notifications">
                  {t('common.refresh', 'Refresh')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBell;
